/* Huge Rugby header: vertical menu panel, mobile drill-down drawer, predictive search drop-down.
   Markup: sections/ss-header.liquid. Results markup: sections/ss-search-results.liquid. */
(() => {
  if (customElements.get('hr-header')) return;

  const DESKTOP = window.matchMedia('(min-width: 1200px)');
  const OPEN_DELAY = 120; // first open: hover intent so sweeping across the nav doesn't flash panels
  const SWITCH_DELAY = 150; // switching to another item while a panel is open
  const CLOSE_DELAY = 350; // grace period after the pointer leaves the header, panel and safe zone
  const L2_DELAY = 120;
  const SEARCH_DEBOUNCE = 200;

  /* Geometry helpers for the hover safe zone */
  const inside = (p, r, pad = 0) => p.x >= r.left - pad && p.x <= r.right + pad && p.y >= r.top - pad && p.y <= r.bottom + pad;
  const corners = (r, pad = 0) => [
    { x: r.left - pad, y: r.top - pad },
    { x: r.right + pad, y: r.top - pad },
    { x: r.right + pad, y: r.bottom + pad },
    { x: r.left - pad, y: r.bottom + pad },
  ];
  const distanceToRect = (p, r) => Math.hypot(Math.max(r.left - p.x, 0, p.x - r.right), Math.max(r.top - p.y, 0, p.y - r.bottom));
  const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  // Point-in-convex-hull test (hull built with the monotone chain algorithm).
  const inHull = (p, points) => {
    const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
    const half = (list) => {
      const out = [];
      list.forEach((q) => {
        while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], q) <= 0) out.pop();
        out.push(q);
      });
      out.pop();
      return out;
    };
    const hull = [...half(pts), ...half([...pts].reverse())];
    if (hull.length < 3) return false;
    return hull.every((a, i) => cross(a, hull[(i + 1) % hull.length], p) >= 0);
  };

  class HrHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.timers = {};
      this.openItem = null;
      this.lastPointer = 'mouse';
      this.bar = this.querySelector('.hr-header__bar');
      this.overlay = this.querySelector('[data-hr-overlay]');
      this.drawer = this.querySelector('[data-hr-drawer]');
      this.search = this.querySelector('[data-hr-search]');
      const opts = { signal: this.abort.signal };

      this.addEventListener('pointerdown', (e) => (this.lastPointer = e.pointerType), opts);
      this.initHeights(opts);
      this.initFit(opts);
      this.initMega(opts);
      this.initDrawer(opts);
      this.initSearch(opts);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      Object.values(this.timers).forEach((t) => clearTimeout(t));
      this.searchController?.abort();
      document.documentElement.classList.remove('hr-lock');
    }

    /* ---------- Header height variables used by Fabric (sticky offsets, hero heights) ---------- */
    initHeights(opts) {
      const update = () => {
        const height = Math.round(this.getBoundingClientRect().height);
        document.body.style.setProperty('--header-height', `${height}px`);
        const group = document.getElementById('header-group');
        if (group) {
          let total = 0;
          for (const el of group.querySelectorAll(':scope > .shopify-section')) total += el.offsetHeight;
          document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
        }
      };
      update();
      this.resizeObserver = new ResizeObserver(() => {
        update();
        this.checkFit();
      });
      this.resizeObserver.observe(this);
      window.addEventListener('resize', () => this.checkFit(), opts);
    }

    /* Fits the header to the space beside the centred logo:
       - nav text (and its em-based gaps) is sized between NAV_MIN and NAV_MAX px to fill the left column;
       - if even NAV_MIN doesn't fit, the burger menu is used instead;
       - the search field shrinks to SEARCH_MIN px, then collapses to an icon, so the basket never overflows. */
    initFit() {
      this.nav = this.querySelector('.hr-nav');
      this.navList = this.querySelector('.hr-nav__list');
      this.left = this.querySelector('.hr-header__left');
      this.right = this.querySelector('.hr-header__right');
      // Re-fit when a column changes size without the header resizing (logo load, basket count bubble, etc.).
      [this.left, this.right, this.right?.querySelector('header-actions')].forEach((el) => el && this.resizeObserver?.observe(el));
      this.checkFit();
      document.fonts?.ready.then(() => this.checkFit());
    }

    checkFit() {
      if (this.fitting) return;
      this.fitting = true;
      try {
        this.fitSearch();
        this.fitNav();
      } finally {
        this.fitting = false;
      }
    }

    fitSearch() {
      const right = this.right;
      const trigger = right?.querySelector('.hr-search-trigger');
      this.classList.remove('hr-header--search-compact');
      if (!right || !trigger || getComputedStyle(trigger).display === 'none') return;
      const SEARCH_MIN = 160;
      const gap = parseFloat(getComputedStyle(right).columnGap) || 0;
      let others = 0;
      let count = 0;
      for (const child of right.children) {
        if (child === trigger) continue;
        const width = child.getBoundingClientRect().width;
        if (width > 0) {
          others += width;
          count += 1;
        }
      }
      const margin = parseFloat(getComputedStyle(trigger).marginInlineEnd) || 0;
      const needed = SEARCH_MIN + margin + others + gap * count + 2;
      if (needed > right.getBoundingClientRect().width) this.classList.add('hr-header--search-compact');
    }

    fitNav() {
      if (!this.nav || !this.navList || !this.left) return;
      const NAV_MIN = 15;
      const NAV_MAX = 18;
      const SAFETY = 12;
      this.classList.remove('hr-header--compact');
      this.style.setProperty('--hr-nav-size', `${NAV_MAX}px`);
      if (!DESKTOP.matches) return;
      let available = this.left.getBoundingClientRect().width - SAFETY;
      for (const child of this.left.children) {
        if (child !== this.nav && child.getBoundingClientRect().width > 0) {
          available -= child.getBoundingClientRect().width;
        }
      }
      // Text, tracking and gaps are all em-based, so the list width scales linearly with the font size.
      const perPx = this.navList.scrollWidth / NAV_MAX;
      const size = perPx > 0 ? Math.floor((available / perPx) * 10) / 10 : NAV_MAX;
      if (size < NAV_MIN) {
        this.classList.add('hr-header--compact');
        this.style.removeProperty('--hr-nav-size');
        this.closeMega();
        return;
      }
      this.style.setProperty('--hr-nav-size', `${Math.min(NAV_MAX, size)}px`);
      // Belt and braces: never let the list overflow its column.
      if (this.navList.scrollWidth > this.left.getBoundingClientRect().width) {
        this.classList.add('hr-header--compact');
        this.closeMega();
      }
    }

    /* ---------- Desktop vertical menu ----------
       Hover model (pointer only; keyboard and touch use the toggle / first tap):
       - Opening: a short hover intent (OPEN_DELAY) so sweeping across the nav doesn't flash panels.
       - Switching: a different item only takes over after SWITCH_DELAY of hover intent, and never while the
         pointer is still travelling towards the open panel.
       - Staying open: while the pointer is over the header row, the panel, or the "safe zone" (the convex hull
         of the point where the pointer left the header row (plus 24px of slack to its right) and the panel's four
         corners, i.e. every straight path from there into the panel). In the safe zone the panel only stays open while the pointer keeps
         moving; if it stalls there, or goes anywhere else, the panel closes after CLOSE_DELAY.
       - Clicking the dimmed page or pressing Esc closes immediately. */
    initMega(opts) {
      this.items = [...this.querySelectorAll('.hr-nav__item')];
      this.items.forEach((item) => {
        const hasPanel = item.hasAttribute('data-hr-panel-item');
        item.addEventListener(
          'pointerenter',
          (e) => {
            if (e.pointerType !== 'mouse' || !this.navActive()) return;
            this.hoverItem = item;
            this.armIntent(item);
          },
          opts
        );
        item.addEventListener(
          'pointerleave',
          (e) => {
            if (e.pointerType !== 'mouse') return;
            if (this.hoverItem === item) this.hoverItem = null;
          },
          opts
        );
        if (!hasPanel) return;

        const link = item.querySelector('.hr-nav__link');
        const toggle = item.querySelector('[data-hr-mega-toggle]');
        // Touch on wide screens: first tap opens the panel, second tap follows the link.
        link?.addEventListener(
          'click',
          (e) => {
            if (this.lastPointer !== 'mouse' && this.navActive() && this.openItem !== item) {
              e.preventDefault();
              this.openMega(item);
            }
          },
          opts
        );
        toggle?.addEventListener(
          'click',
          () => {
            if (this.openItem === item) {
              this.closeMega();
              toggle.focus();
            } else {
              this.openMega(item);
              item.querySelector('.hr-mega .hr-mega__l1')?.focus();
            }
          },
          opts
        );

        const panel = item.querySelector('[data-hr-mega-panel]');
        panel?.querySelectorAll('.hr-mega__col--l1 .hr-mega__l1').forEach((entry) => {
          const isTrigger = entry.hasAttribute('data-hr-l2-trigger');
          entry.addEventListener(
            'pointerenter',
            (e) => {
              if (e.pointerType !== 'mouse') return;
              clearTimeout(this.timers.l2);
              this.timers.l2 = setTimeout(() => (isTrigger ? this.openL2(panel, entry) : this.closeL2(panel)), L2_DELAY);
            },
            opts
          );
          if (isTrigger) {
            entry.addEventListener(
              'click',
              () => {
                clearTimeout(this.timers.l2);
                const isOpen = entry.getAttribute('aria-expanded') === 'true';
                if (isOpen && this.lastPointer !== 'mouse') {
                  this.closeL2(panel);
                  return;
                }
                this.openL2(panel, entry);
                if (this.lastPointer !== 'mouse') {
                  document.getElementById(entry.getAttribute('aria-controls'))?.querySelector('a')?.focus();
                }
              },
              opts
            );
          }
        });
      });

      document.addEventListener('pointermove', (e) => this.onPointerMove(e), { ...opts, passive: true });
      document.documentElement.addEventListener(
        'mouseleave',
        () => {
          this.hoverItem = null;
          clearTimeout(this.timers.open);
          if (this.openItem && this.openedByPointer) this.scheduleClose();
        },
        opts
      );
      this.overlay?.addEventListener('click', () => this.closeMega(), opts);
      document.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Escape' || !this.openItem) return;
          const item = this.openItem;
          const l2Open = item.querySelector('[data-hr-l2-trigger][aria-expanded="true"]');
          if (l2Open && item.querySelector('[data-hr-l2]:not([hidden])')?.contains(document.activeElement)) {
            this.closeL2(item.querySelector('[data-hr-mega-panel]'));
            l2Open.focus();
            return;
          }
          this.closeMega();
          item.querySelector('[data-hr-mega-toggle]')?.focus();
        },
        opts
      );
      this.addEventListener(
        'focusout',
        (e) => {
          if (this.openItem && e.relatedTarget && !this.openItem.contains(e.relatedTarget)) this.closeMega();
        },
        opts
      );
      DESKTOP.addEventListener('change', () => this.closeMega(), opts);
    }

    navActive() {
      return DESKTOP.matches && !this.classList.contains('hr-header--compact');
    }

    /* Hover intent: open (or switch to) an item only if the pointer is still resting on it after the delay */
    armIntent(item, delay) {
      clearTimeout(this.timers.open);
      if (this.openItem === item) {
        this.cancelClose();
        return;
      }
      const wait = delay ?? (this.openItem ? SWITCH_DELAY : OPEN_DELAY);
      this.timers.open = setTimeout(() => {
        if (this.hoverItem !== item || !this.navActive()) return;
        // Still heading for the open panel (e.g. a diagonal sweep across neighbouring items): look again shortly.
        if (this.openItem && this.isAimingAtPanel()) {
          this.armIntent(item, 80);
          return;
        }
        if (item.hasAttribute('data-hr-panel-item')) this.openMega(item, { byPointer: true });
        else if (this.openItem) this.closeMega();
      }, wait);
    }

    onPointerMove(e) {
      if (e.pointerType !== 'mouse') return;
      const point = { x: e.clientX, y: e.clientY, t: e.timeStamp || performance.now() };
      // Keep the previous sample from at least ~16ms earlier so direction is meaningful at high event rates.
      if (!this.pointer || point.t - this.pointer.t > 16) this.prevPointer = this.pointer;
      this.pointer = point;
      if (!this.openItem || !this.openedByPointer) return;

      const panelRect = this.openPanelRect();
      if (!panelRect) return;
      const barRect = this.bar.getBoundingClientRect();
      if (inside(point, barRect)) {
        this.exitPoint = { x: point.x, y: point.y };
        this.cancelClose();
        return;
      }
      if (inside(point, panelRect, 1)) {
        this.cancelClose();
        return;
      }
      if (this.exitPoint && inHull(point, [this.exitPoint, { x: this.exitPoint.x + 24, y: this.exitPoint.y }, ...corners(panelRect, 6)])) {
        // Travelling towards the panel: keep it open, but only while the pointer keeps moving.
        this.scheduleClose(true);
        return;
      }
      this.scheduleClose(false);
    }

    openPanelRect() {
      const panel = this.openItem?.querySelector('[data-hr-mega-panel]');
      return panel && !panel.hidden ? panel.getBoundingClientRect() : null;
    }

    /* True if the last movement was recent and reduced the distance to the open panel */
    isAimingAtPanel() {
      const rect = this.openPanelRect();
      const now = this.pointer;
      const before = this.prevPointer;
      if (!rect || !now || !before) return false;
      if (performance.now() - now.t > 100) return false;
      return distanceToRect(now, rect) < distanceToRect(before, rect) - 0.5;
    }

    cancelClose() {
      clearTimeout(this.timers.close);
      this.timers.close = null;
    }

    /* reset=true restarts the countdown (used while moving through the safe zone); false keeps a running one */
    scheduleClose(reset = true) {
      if (!reset && this.timers.close) return;
      clearTimeout(this.timers.close);
      this.timers.close = setTimeout(() => {
        this.timers.close = null;
        this.closeMega();
      }, CLOSE_DELAY);
    }

    openMega(item, { byPointer = false } = {}) {
      this.cancelClose();
      this.openedByPointer = byPointer;
      if (this.openItem === item) return;
      if (this.openItem) this.closeMega({ keepOverlay: true });
      this.style.setProperty('--hr-header-bottom', `${Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom))}px`);
      const panel = item.querySelector('[data-hr-mega-panel]');
      const toggle = item.querySelector('[data-hr-mega-toggle]');
      if (!panel) return;
      panel.hidden = false;
      item.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');
      if (this.overlay) this.overlay.hidden = false;
      this.openItem = item;
      if (byPointer && this.pointer) this.exitPoint = { x: this.pointer.x, y: this.pointer.y };
    }

    closeMega({ keepOverlay = false } = {}) {
      clearTimeout(this.timers.open);
      this.cancelClose();
      clearTimeout(this.timers.l2);
      const item = this.openItem;
      if (item) {
        const panel = item.querySelector('[data-hr-mega-panel]');
        if (panel) {
          this.closeL2(panel);
          panel.hidden = true;
        }
        item.classList.remove('is-open');
        item.querySelector('[data-hr-mega-toggle]')?.setAttribute('aria-expanded', 'false');
      }
      this.openItem = null;
      if (!keepOverlay) {
        this.exitPoint = null;
        if (this.overlay) this.overlay.hidden = true;
      }
    }

    openL2(panel, trigger) {
      const target = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!target) return;
      panel.querySelectorAll('[data-hr-l2]').forEach((col) => {
        if (col !== target) col.hidden = true;
      });
      panel.querySelectorAll('[data-hr-l2-trigger]').forEach((btn) => btn.setAttribute('aria-expanded', String(btn === trigger)));
      target.hidden = false;
    }

    closeL2(panel) {
      if (!panel) return;
      panel.querySelectorAll('[data-hr-l2]').forEach((col) => (col.hidden = true));
      panel.querySelectorAll('[data-hr-l2-trigger]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    }

    /* ---------- Mobile drawer (drill-down) ---------- */
    initDrawer(opts) {
      if (!this.drawer) return;
      this.querySelectorAll('[data-hr-drawer-open]').forEach((btn) =>
        btn.addEventListener(
          'click',
          () => {
            this.closeMega();
            this.showView('root');
            this.drawer.showModal();
            document.documentElement.classList.add('hr-lock');
            btn.setAttribute('aria-expanded', 'true');
          },
          opts
        )
      );
      this.drawer.querySelectorAll('[data-hr-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.drawer.close(), opts)
      );
      this.drawer.addEventListener(
        'click',
        (e) => {
          if (e.target === this.drawer) this.drawer.close();
        },
        opts
      );
      this.drawer.addEventListener(
        'close',
        () => {
          document.documentElement.classList.remove('hr-lock');
          this.querySelectorAll('[data-hr-drawer-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
        },
        opts
      );
      this.drawer.querySelectorAll('[data-hr-view-target]').forEach((btn) =>
        btn.addEventListener('click', () => this.showView(btn.dataset.hrViewTarget, 'forward'), opts)
      );
      this.drawer.querySelectorAll('[data-hr-view-back]').forEach((btn) =>
        btn.addEventListener('click', () => this.showView(btn.dataset.hrViewBack, 'back', btn.closest('[data-hr-view]')?.dataset.hrView), opts)
      );
    }

    showView(id, direction, fromId) {
      const views = this.drawer.querySelectorAll('[data-hr-view]');
      let shown = null;
      views.forEach((view) => {
        const active = view.dataset.hrView === id;
        view.hidden = !active;
        view.classList.remove('is-forward', 'is-back');
        if (active) {
          shown = view;
          if (direction) view.classList.add(direction === 'back' ? 'is-back' : 'is-forward');
        }
      });
      this.drawer.querySelector('.hr-drawer__views')?.scrollTo({ top: 0 });
      if (!direction || !shown) return;
      const focusTarget =
        direction === 'back' && fromId
          ? shown.querySelector(`[data-hr-view-target="${fromId}"]`)
          : shown.querySelector('.hr-drawer__back, a, button');
      focusTarget?.focus();
    }

    /* ---------- Search ---------- */
    initSearch(opts) {
      const dialog = this.search;
      if (!dialog) return;
      this.searchInput = dialog.querySelector('[data-hr-search-input]');
      this.searchDefault = dialog.querySelector('[data-hr-search-default]');
      this.searchResults = dialog.querySelector('[data-hr-search-results]');
      this.searchBody = dialog.querySelector('.hr-search__body');
      this.searchClear = dialog.querySelector('[data-hr-search-clear]');
      this.searchSeq = 0;

      this.querySelectorAll('[data-hr-search-open]').forEach((btn) =>
        btn.addEventListener(
          'click',
          () => {
            this.closeMega();
            this.searchTrigger = btn;
            dialog.showModal();
            document.documentElement.classList.add('hr-lock');
            this.searchInput?.focus();
            this.searchInput?.select();
          },
          opts
        )
      );
      dialog.querySelector('[data-hr-search-close]')?.addEventListener('click', () => dialog.close(), opts);
      // One Esc closes the panel, even when the input has text (Chrome would otherwise clear the field first).
      dialog.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Escape') return;
          e.preventDefault();
          dialog.close();
        },
        opts
      );
      dialog.addEventListener(
        'click',
        (e) => {
          if (e.target === dialog) dialog.close();
        },
        opts
      );
      dialog.addEventListener(
        'close',
        () => {
          document.documentElement.classList.remove('hr-lock');
          this.searchController?.abort();
          this.searchTrigger?.focus();
        },
        opts
      );
      this.searchInput?.addEventListener('input', () => this.onSearchInput(), opts);
      this.searchClear?.addEventListener(
        'click',
        () => {
          this.searchInput.value = '';
          this.onSearchInput();
          this.searchInput.focus();
        },
        opts
      );
      // Stash 9 Oct 2026: best sellers no longer loaded – the search opens as a slim input bar (see ss-header.css).
    }

    async loadBestSellers() {
      const url = this.search?.dataset.bestsellersUrl;
      const target = this.search?.querySelector('[data-hr-bestsellers]');
      if (!url || !target || this.bestSellersLoaded) return;
      this.bestSellersLoaded = true;
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = doc.querySelector('.hr-sr');
        if (items && items.querySelector('li')) target.innerHTML = items.innerHTML;
        else target.closest('.hr-search__main')?.setAttribute('hidden', '');
      } catch (error) {
        this.bestSellersLoaded = false;
      }
    }

    onSearchInput() {
      const term = this.searchInput.value.trim();
      if (this.searchClear) this.searchClear.hidden = this.searchInput.value.length === 0;
      clearTimeout(this.timers.search);
      if (!term) {
        this.searchController?.abort();
        this.searchSeq += 1;
        this.searchResults.hidden = true;
        this.searchResults.innerHTML = '';
        this.searchDefault.hidden = false;
        this.searchBody.classList.remove('is-loading');
        return;
      }
      this.timers.search = setTimeout(() => this.fetchResults(term), SEARCH_DEBOUNCE);
    }

    async fetchResults(term) {
      const base = this.search.dataset.suggestUrl || '/search/suggest';
      const params = new URLSearchParams({
        q: term,
        section_id: 'ss-search-results',
        'resources[type]': 'product,query,collection',
        'resources[limit]': '4',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
      });
      this.searchController?.abort();
      this.searchController = new AbortController();
      const seq = ++this.searchSeq;
      this.searchBody.classList.add('is-loading');
      try {
        const response = await fetch(`${base}?${params.toString()}`, { signal: this.searchController.signal });
        if (!response.ok) throw new Error(response.status);
        const html = await response.text();
        if (seq !== this.searchSeq) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('.hr-sr');
        if (!content) return;
        this.searchResults.innerHTML = content.innerHTML;
        this.highlight(term);
        this.searchDefault.hidden = true;
        this.searchResults.hidden = false;
      } catch (error) {
        if (error.name === 'AbortError') return;
      } finally {
        if (seq === this.searchSeq) this.searchBody.classList.remove('is-loading');
      }
    }

    /* Bold the part of each suggestion that matches what was typed (Gymshark style) */
    highlight(term) {
      const needle = term.toLowerCase();
      const words = needle.split(/\s+/).filter(Boolean);
      this.searchResults.querySelectorAll('[data-hr-hl]').forEach((el) => {
        const text = el.dataset.hrHl;
        const lower = text.toLowerCase();
        const marks = new Array(text.length).fill(false);
        const markAll = (w) => {
          let i = lower.indexOf(w);
          while (w && i !== -1) {
            for (let k = i; k < i + w.length; k += 1) marks[k] = true;
            i = lower.indexOf(w, i + w.length);
          }
        };
        if (lower.includes(needle)) markAll(needle);
        else words.forEach(markAll);
        el.textContent = '';
        let buffer = '';
        let bold = marks[0];
        const flush = () => {
          if (!buffer) return;
          if (bold) {
            const strong = document.createElement('strong');
            strong.textContent = buffer;
            el.appendChild(strong);
          } else {
            el.appendChild(document.createTextNode(buffer));
          }
          buffer = '';
        };
        for (let i = 0; i < text.length; i += 1) {
          if (marks[i] !== bold) {
            flush();
            bold = marks[i];
          }
          buffer += text[i];
        }
        flush();
      });
    }
  }

  customElements.define('hr-header', HrHeader);
})();
